import { useState } from 'react';
import type { AttributeDefinitionAdminResponse, AttributeLibraryResponse } from '../../api/types';
import { apiPatch, apiPost, describeError } from '../../api/client';
import { useApiResource } from '../../hooks/useApiResource';
import { ErrorState, LoadingState } from '../StateMessage';
import { ActionFeedback, AdminForm, ConfirmAction, Field, StatusTag, useAction } from './adminShared';
import styles from './admin.module.css';

/**
 * The attribute library (Mission 018).
 *
 * ---------------------------------------------------------------------
 * THE ONE RULE THIS SCREEN EXISTS TO CARRY
 *
 * `docs/DOMAIN_MODEL.md` §2.5 splits edits in two, and the split is not
 * cosmetic-versus-important — it is "safe to do in place" versus "would
 * silently restate every rating already recorded":
 *
 * - The **scale wording** (what 1 and 20 mean) is disabled the moment any
 *   assessment exists, and the form says why rather than failing on submit.
 *   The server refuses it regardless; this is the form declining to offer
 *   what it knows will be refused.
 * - A **wording change** to the name or definition requires the
 *   administrator to confirm it is cosmetic. The system cannot tell a typo
 *   fix from a redefinition; the checkbox is the person saying which it is,
 *   and the audit trail records that they said it.
 *
 * The alternative to deprecate-and-replace is not offered anywhere, because
 * it does not exist: there is no way to edit an attribute's meaning in
 * place, and a form that appeared to allow it would be lying.
 * ---------------------------------------------------------------------
 */
export function AttributeLibrarySection() {
  const library = useApiResource<AttributeLibraryResponse>('/admin/attribute-definitions');
  const [editing, setEditing] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  return (
    <section className={styles.section} aria-label="Attribute library">
      <h2 className={styles.sectionTitle}>Attribute library</h2>
      <p className={styles.sectionNote}>
        The attributes everybody in this organisation is assessed against. Retired attributes stay listed here and
        keep the assessments recorded against them — nothing in the library is ever deleted.
      </p>

      {library.status === 'loading' ? <LoadingState label="Loading the attribute library…" /> : null}
      {library.status === 'failed' ? (
        <ErrorState title="The attribute library could not be loaded" detail={describeError(library.error)} />
      ) : null}

      {library.status === 'loaded' ? (
        <>
          <ul className={styles.list}>
            {library.data.attributes.map((attribute) => (
              <li key={attribute.id} className={styles.row}>
                <span className={styles.rowName}>{attribute.name}</span>
                <StatusTag status={attribute.status} />
                <span className={styles.rowMeta}>
                  {attribute.categoryLabel} · {attribute.assessmentCount}{' '}
                  {attribute.assessmentCount === 1 ? 'assessment' : 'assessments'} recorded
                  {attribute.openRequirementCount > 0
                    ? ` · required by ${attribute.openRequirementCount} role ${
                        attribute.openRequirementCount === 1 ? 'requirement' : 'requirements'
                      }`
                    : ''}
                </span>
                <span className={styles.rowActions}>
                  <button
                    type="button"
                    className={styles.secondaryButton}
                    onClick={() => setEditing(editing === attribute.id ? null : attribute.id)}
                  >
                    {editing === attribute.id ? 'Close' : 'Edit'}
                  </button>
                </span>
                {editing === attribute.id ? (
                  <AttributeEditor
                    attribute={attribute}
                    onDone={() => {
                      setEditing(null);
                      library.reload();
                    }}
                  />
                ) : null}
              </li>
            ))}
          </ul>

          {creating ? (
            <AttributeCreator
              onDone={() => {
                setCreating(false);
                library.reload();
              }}
            />
          ) : (
            <div className={styles.actions}>
              <button type="button" className={styles.primaryButton} onClick={() => setCreating(true)}>
                Define a new attribute
              </button>
            </div>
          )}
        </>
      ) : null}
    </section>
  );
}

function AttributeCreator({ onDone }: { onDone: () => void }) {
  const [name, setName] = useState('');
  const [definition, setDefinition] = useState('');
  const [categoryCode, setCategoryCode] = useState('work_related');
  const [lowEndMeaning, setLowEndMeaning] = useState('');
  const [highEndMeaning, setHighEndMeaning] = useState('');
  const { state, run } = useAction();

  async function submit() {
    const ok = await run(async () => {
      await apiPost('/admin/attribute-definitions', {
        name,
        definition,
        categoryCode,
        lowEndMeaning,
        highEndMeaning,
      });
      return `"${name}" was added to the library.`;
    });
    if (ok) onDone();
  }

  return (
    <AdminForm title="Define a new attribute" onSubmit={submit} submitLabel="Create attribute" busy={state.status === 'saving'}>
      <Field id="new-attribute-name" label="Name" value={name} onChange={setName} />
      <Field
        id="new-attribute-definition"
        label="Definition"
        value={definition}
        onChange={setDefinition}
        multiline
        hint="What this attribute measures. Check it against the existing library for overlap before adding it."
      />
      <Field id="new-attribute-category" label="Category code" value={categoryCode} onChange={setCategoryCode} />
      <Field
        id="new-attribute-low"
        label="What a rating of 1 means"
        value={lowEndMeaning}
        onChange={setLowEndMeaning}
        hint="Fixed once the first assessment is recorded — it is what every rating is read against."
      />
      <Field id="new-attribute-high" label="What a rating of 20 means" value={highEndMeaning} onChange={setHighEndMeaning} />
      <ActionFeedback state={state} />
    </AdminForm>
  );
}

function AttributeEditor({
  attribute,
  onDone,
}: {
  attribute: AttributeDefinitionAdminResponse;
  onDone: () => void;
}) {
  const [name, setName] = useState(attribute.name);
  const [definition, setDefinition] = useState(attribute.definition);
  const [lowEndMeaning, setLowEndMeaning] = useState(attribute.lowEndMeaning);
  const [highEndMeaning, setHighEndMeaning] = useState(attribute.highEndMeaning);
  const [attested, setAttested] = useState(false);
  const { state, run } = useAction();

  const wordingChanged = name !== attribute.name || definition !== attribute.definition;

  async function save() {
    const ok = await run(async () => {
      await apiPatch(`/admin/attribute-definitions/${attribute.id}`, {
        name,
        definition,
        ...(attribute.scaleWordingEditable ? { lowEndMeaning, highEndMeaning } : {}),
        ...(wordingChanged ? { attestedCosmetic: attested } : {}),
      });
      return `"${name}" was updated.`;
    });
    if (ok) onDone();
  }

  async function deprecate() {
    const ok = await run(async () => {
      await apiPost(`/admin/attribute-definitions/${attribute.id}/deprecation`, {});
      return `"${attribute.name}" was retired from the library. Its recorded assessments are unchanged.`;
    });
    if (ok) onDone();
  }

  async function reactivate() {
    const ok = await run(async () => {
      await apiPost(`/admin/attribute-definitions/${attribute.id}/reactivation`, {});
      return `"${attribute.name}" was reactivated.`;
    });
    if (ok) onDone();
  }

  return (
    <AdminForm title={`Edit ${attribute.name}`} onSubmit={save} submitLabel="Save changes" busy={state.status === 'saving'}>
      <Field id={`name-${attribute.id}`} label="Name" value={name} onChange={setName} />
      <Field id={`definition-${attribute.id}`} label="Definition" value={definition} onChange={setDefinition} multiline />

      <Field
        id={`low-${attribute.id}`}
        label="What a rating of 1 means"
        value={lowEndMeaning}
        onChange={setLowEndMeaning}
        disabled={!attribute.scaleWordingEditable}
        disabledReason={
          attribute.scaleWordingEditable
            ? undefined
            : `Fixed: ${attribute.assessmentCount} ${
                attribute.assessmentCount === 1 ? 'assessment has' : 'assessments have'
              } been recorded against this attribute, and changing what 1 and 20 mean would restate every one of them. To change the meaning, retire this attribute and define its replacement.`
        }
      />
      <Field
        id={`high-${attribute.id}`}
        label="What a rating of 20 means"
        value={highEndMeaning}
        onChange={setHighEndMeaning}
        disabled={!attribute.scaleWordingEditable}
      />

      {wordingChanged ? (
        <div className={styles.checkboxRow}>
          <input
            id={`attested-${attribute.id}`}
            type="checkbox"
            checked={attested}
            onChange={(event) => setAttested(event.target.checked)}
          />
          <label className={styles.checkboxLabel} htmlFor={`attested-${attribute.id}`}>
            This change is cosmetic — a typo fix, formatting, or a clarification that does not change what is being
            measured. If it changes the meaning, retire this attribute and define a replacement instead.
          </label>
        </div>
      ) : null}

      <ActionFeedback state={state} />

      {attribute.status === 'active' ? (
        <ConfirmAction
          label="Retire this attribute"
          confirmLabel="Retire it"
          busy={state.status === 'saving'}
          description={
            <>
              Retiring <strong>{attribute.name}</strong> removes it from the list of attributes people can be assessed
              against. It is <strong>not</strong> deleted: the {attribute.assessmentCount}{' '}
              {attribute.assessmentCount === 1 ? 'assessment' : 'assessments'} already recorded against it stay exactly
              as they are and stay visible on those people’s profiles.
              {attribute.openRequirementCount > 0
                ? ` ${attribute.openRequirementCount} role ${
                    attribute.openRequirementCount === 1 ? 'requirement' : 'requirements'
                  } still ask for it.`
                : ''}
            </>
          }
          onConfirm={deprecate}
        />
      ) : (
        <>
          <p className={styles.locked}>
            This attribute is retired. Its recorded assessments remain readable on the profiles of the people who
            have them.
          </p>
          <div className={styles.actions}>
            <button type="button" className={styles.secondaryButton} onClick={reactivate} disabled={state.status === 'saving'}>
              {state.status === 'saving' ? 'Working…' : 'Reactivate this attribute'}
            </button>
          </div>
        </>
      )}
    </AdminForm>
  );
}
